import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Gift } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEEN_KEY_PREFIX = "cosmic_daily_login_seen_";

/**
 * Daily Login Rewards modal — surfaces once per UTC day on /levels.
 * Shows a 7-day rotating moonstone reward cycle (10/15/20/30/40/60/100).
 * Loss-aversion copy emphasises the day-7 100☾ payout to keep the
 * pilot returning. Dismiss persists per-day so the same banner
 * doesn't reappear on every nav.
 */
export default function DailyLoginModal() {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(null); // { awarded, day_index, new_balance }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/daily-login/status`, { withCredentials: true });
        if (cancelled) return;
        setStatus(r.data);
        const today = new Date().toISOString().slice(0, 10);
        const seenKey = `${SEEN_KEY_PREFIX}${today}`;
        if (r.data.available && !localStorage.getItem(seenKey)) {
          setOpen(true);
        }
      } catch {
        /* unauthenticated → no modal */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dismiss = () => {
    const today = new Date().toISOString().slice(0, 10);
    localStorage.setItem(`${SEEN_KEY_PREFIX}${today}`, "1");
    setOpen(false);
  };

  const claim = async () => {
    if (claiming) return;
    setClaiming(true);
    try {
      const r = await axios.post(`${API}/daily-login/claim`, {}, { withCredentials: true });
      setClaimed(r.data);
      toast.success(`+${r.data.awarded} ☾ Daily reward claimed!`, {
        description: `Day ${r.data.day_index} of 7 · Tomorrow: ${r.data.next_reward} ☾`,
        duration: 4500,
      });
      const today = new Date().toISOString().slice(0, 10);
      localStorage.setItem(`${SEEN_KEY_PREFIX}${today}`, "1");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't claim — try again.");
    } finally {
      setClaiming(false);
    }
  };

  if (!open || !status) return null;
  const cycle = status.cycle || [10, 15, 20, 30, 40, 60, 100];
  const dayIndex = claimed ? claimed.day_index : status.day_index;
  const isMaxDay = dayIndex === 7;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="daily-login-modal"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative w-full max-w-md border border-[#ffd700]/60 bg-gradient-to-b from-[#0a0716] to-[#05050a]"
        style={{ boxShadow: "0 0 60px rgba(255,215,0,0.28)" }}
      >
        <button
          onClick={dismiss}
          className="absolute top-2 right-2 text-white/60 hover:text-white p-1"
          data-testid="daily-login-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="px-6 pt-6 pb-3 border-b border-[#ffd700]/20 flex items-center gap-3">
          <Gift className="w-5 h-5 text-[#ffd700]" />
          <div>
            <div className="font-mono-cosmic text-[10px] tracking-[0.3em] text-[#ffd700]/80 uppercase">
              {claimed ? "// CLAIMED" : "// DAILY REWARD"}
            </div>
            <div className="font-heading text-xl sm:text-2xl tracking-tight text-white">
              {claimed ? "Reward Banked" : "Daily Login Reward"}
            </div>
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* 7-day cycle row */}
          <div className="grid grid-cols-7 gap-1.5" data-testid="daily-login-cycle">
            {cycle.map((reward, idx) => {
              const day = idx + 1;
              const isToday = day === dayIndex;
              const isClaimedToday = claimed && day === claimed.day_index;
              const isPast = day < dayIndex;
              return (
                <div
                  key={day}
                  className={`relative flex flex-col items-center justify-center py-2.5 border text-center transition-colors ${
                    isToday
                      ? "border-[#ffd700] bg-[#ffd700]/15"
                      : isPast
                        ? "border-white/15 bg-white/5"
                        : "border-white/10 bg-black/30"
                  } ${isClaimedToday ? "daily-login-pulse" : ""}`}
                  data-testid={`daily-login-day-${day}`}
                  data-active={isToday ? "1" : "0"}
                >
                  <span
                    className="font-mono-cosmic text-[8px] tracking-[0.15em] uppercase"
                    style={{ color: isToday ? "#ffd700" : "rgba(255,255,255,0.4)" }}
                  >
                    D{day}
                  </span>
                  <span
                    className="font-heading text-sm tabular-nums leading-none mt-0.5"
                    style={{
                      color: day === 7
                        ? "#ffd700"
                        : isToday ? "#fff" : "rgba(255,255,255,0.55)",
                    }}
                  >
                    {reward}
                  </span>
                  <span
                    className="font-mono-cosmic text-[8px] leading-none mt-0.5"
                    style={{ color: isToday ? "#ffd700" : "rgba(255,255,255,0.35)" }}
                  >
                    ☾
                  </span>
                </div>
              );
            })}
          </div>

          {/* Hero copy */}
          {!claimed ? (
            <div className="text-center space-y-2">
              <div className="font-mono-cosmic text-[10px] tracking-[0.3em] text-[#ffd700]/80 uppercase">
                TODAY · DAY {dayIndex} OF 7
              </div>
              <div className="font-heading text-3xl sm:text-4xl tracking-tight text-[#ffd700]">
                +{cycle[dayIndex - 1]} <span className="text-2xl">☾</span>
              </div>
              {isMaxDay ? (
                <p className="text-xs sm:text-sm text-white/70">
                  Day 7 jackpot · 100 moonstones. Streak resets tomorrow.
                </p>
              ) : (
                <p className="text-xs sm:text-sm text-white/70">
                  Don't miss tomorrow — Day {dayIndex + 1} pays{" "}
                  <span className="text-[#ffd700] font-semibold">
                    {cycle[dayIndex] || 10} ☾
                  </span>
                </p>
              )}
            </div>
          ) : (
            <div className="text-center space-y-1">
              <div className="font-heading text-3xl sm:text-4xl tracking-tight text-[#ffd700]">
                +{claimed.awarded} <span className="text-2xl">☾</span>
              </div>
              <p className="text-xs sm:text-sm text-white/70">
                Wallet: <span className="text-white tabular-nums">{claimed.new_balance} ☾</span>
                {" · "}
                Tomorrow:{" "}
                <span className="text-[#ffd700] tabular-nums">{claimed.next_reward} ☾</span>
              </p>
            </div>
          )}

          {/* CTA */}
          <div className="pt-1">
            {!claimed ? (
              <button
                onClick={claim}
                disabled={claiming}
                className="w-full py-3 border border-[#ffd700] bg-gradient-to-r from-[#ffd700]/20 to-[#ff007f]/20 hover:from-[#ffd700]/30 hover:to-[#ff007f]/30 transition-colors font-heading uppercase tracking-[0.2em] text-sm text-[#ffd700] disabled:opacity-50 disabled:cursor-not-allowed daily-login-pulse"
                data-testid="daily-login-claim-btn"
              >
                {claiming ? "Claiming…" : "Claim Reward"}
              </button>
            ) : (
              <button
                onClick={dismiss}
                className="w-full py-3 border border-white/30 hover:border-white/60 transition-colors font-heading uppercase tracking-[0.2em] text-sm text-white"
                data-testid="daily-login-dismiss-btn"
              >
                Continue
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
